import { useEffect, useRef } from 'react';

/**
 * Runs `frame` once per animation frame while the document scrolls or
 * resizes, and once on mount / whenever `revision` changes. A frame returning
 * true is re-run on the next animation frame (for animations settling after
 * the scroll stopped). The capture-phase document listener catches the scroll
 * wherever it happens - this site makes <body> the scroller, not window.
 */
export const useScrollFrame = (
  frame: () => boolean | void,
  revision: unknown
) => {
  const latest = useRef(frame);
  latest.current = frame;

  useEffect(() => {
    let raf = 0;
    const run = () => {
      raf = 0;
      if (latest.current() === true) schedule();
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(run);
    };
    const opts: AddEventListenerOptions = { passive: true, capture: true };
    document.addEventListener('scroll', schedule, opts);
    window.addEventListener('resize', schedule, { passive: true });
    schedule();
    return () => {
      document.removeEventListener('scroll', schedule, opts);
      window.removeEventListener('resize', schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [revision]);
};
